import { useId, useState, type ReactNode } from 'react';
import { useForm } from '@tanstack/react-form';
import { Modal } from '../../atoms/Modal';
import { Input } from '../../atoms/Input';
import { ModalFooter } from '../../molecules/ModalFooter';
import { SegmentedControl } from '../../molecules/SegmentedControl';
import { getFieldError } from '../../../lib';
import { passwordChangeSchema, passwordChangeDefaults } from './passwordChange.schema';
import * as s from './PasswordChangeModal.css';

type SecuritySegment = 'password' | 'passkey';

const SECURITY_SEGMENTS = [
  { id: 'password', label: '비밀번호' },
  { id: 'passkey', label: '패스키' },
] as const satisfies readonly { id: SecuritySegment; label: string }[];

export interface PasswordChangeModalProps {
  open: boolean;
  onComplete: (input: { currentPassword: string; newPassword: string }) => void;
  onClose: () => void;
  loading?: boolean;
  error?: string;
  /**
   * 패스키 관리 패널. 넘기면 모달이 "비밀번호 · 패스키" 두 세그먼트가 되고 한 번에 하나만 보인다.
   * "변경" 발판은 비밀번호 세그먼트에서만 뜬다 — 패스키를 만지다 빈 비밀번호 폼을 제출하는 사고를 막는다.
   */
  passkeyPanel?: ReactNode;
}

export function PasswordChangeModal({
  open,
  onComplete,
  onClose,
  loading = false,
  error,
  passkeyPanel,
}: PasswordChangeModalProps) {
  const [segment, setSegment] = useState<SecuritySegment>('password');
  const panelId = useId();
  const showPasswordForm = passkeyPanel == null || segment === 'password';

  const form = useForm({
    defaultValues: passwordChangeDefaults,
    validators: {
      onSubmit: passwordChangeSchema,
      onChange: passwordChangeSchema,
    },
    onSubmit: ({ value }) => {
      onComplete({
        currentPassword: value.currentPassword,
        newPassword: value.newPassword,
      });
    },
  });

  const handleClose = () => {
    form.reset();
    setSegment('password');
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={passkeyPanel != null ? '비밀번호 · 패스키' : '비밀번호 변경'}
      footer={
        showPasswordForm ? (
          <ModalFooter
            primaryLabel={loading ? '변경 중...' : '변경'}
            onPrimaryClick={() => void form.handleSubmit()}
            onSecondaryClick={handleClose}
            primaryDisabled={loading}
          />
        ) : undefined
      }
    >
      {passkeyPanel != null && (
        <div className={s.segments}>
          <SegmentedControl
            segments={SECURITY_SEGMENTS}
            activeId={segment}
            onSelect={setSegment}
            ariaLabel="보안 설정 항목"
            panelIdById={{ password: `${panelId}-password`, passkey: `${panelId}-passkey` }}
          />
        </div>
      )}
      {passkeyPanel != null && segment === 'passkey' && (
        <div id={`${panelId}-passkey`} role="tabpanel">
          {passkeyPanel}
        </div>
      )}
      {/* 값은 useForm 스토어에 있어 세그먼트를 오가도 입력이 남는다 — hidden 은 s.form 의 display:flex 에 진다 */}
      {showPasswordForm && (
        <form
          id={`${panelId}-password`}
          role={passkeyPanel != null ? 'tabpanel' : undefined}
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
          className={s.form}
        >
          {error && (
            <div className={s.errorBox}>
              <span className={s.errorText}>{error}</span>
            </div>
          )}
          <form.Field name="currentPassword">
            {(field) => (
              <Input
                label="현재 비밀번호"
                fullWidth
                type="password"
                autoComplete="current-password"
                placeholder="현재 비밀번호를 입력하세요"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                onBlur={field.handleBlur}
                error={getFieldError(field.state.meta.errors, field.state.meta.isTouched)}
                disabled={loading}
              />
            )}
          </form.Field>
          <form.Field name="newPassword">
            {(field) => (
              <Input
                label="새 비밀번호"
                fullWidth
                type="password"
                autoComplete="new-password"
                placeholder="새 비밀번호를 입력하세요 (8자 이상)"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                onBlur={field.handleBlur}
                error={getFieldError(field.state.meta.errors, field.state.meta.isTouched)}
                disabled={loading}
              />
            )}
          </form.Field>
          <form.Field name="confirmPassword">
            {(field) => (
              <Input
                label="새 비밀번호 확인"
                fullWidth
                type="password"
                autoComplete="new-password"
                placeholder="새 비밀번호를 다시 입력하세요"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                onBlur={field.handleBlur}
                error={getFieldError(field.state.meta.errors, field.state.meta.isTouched)}
                disabled={loading}
              />
            )}
          </form.Field>
        </form>
      )}
    </Modal>
  );
}
